import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ChildDetails from './Steps/ChildDetails';
import PlatformOnboarding from './PlatformOnboarding';
import { AddPaymentMethod } from '../AddPaymentMethod';
import { Alert, AlertTitle, AlertDescription } from '@/components/shadcn/alert';
import { AlertCircle } from 'lucide-react';
import { get, post } from '../../utils/api';
import Cookies from 'js-cookie';
import { verifyToken } from '../../services/authService';
import { ONBOARDING_STEPS } from '../../utils/constants/constants';

const PLATFORM_PLANS = ['platform_only', 'platform_only_family'];

function OnboardingFlow() {
  const { user, login } = useAuth();
  const [childDetails, setChildDetails] = useState(null);
  // 1-1 plan only: schedule is saved, waiting on card details before the trial starts.
  const [awaitingCard, setAwaitingCard] = useState(false);
  const [cardStepError, setCardStepError] = useState('');
  const [finalising, setFinalising] = useState(false);

  const navigate = useNavigate();

  const isPlatformPlan = PLATFORM_PLANS.includes(user?.selectedPlan);
  const isOneToOnePlan = user?.selectedPlan === 'one_to_one';

  // Facebook-lead parents have child 1's year group from the lead form. Pass it as a
  // seed so ChildDetails pre-selects it (still editable). Empty for normal signups —
  // ChildDetails validates it against its own YEAR_GROUPS before using it.
  const leadYearGroupSeed = user?.isLeadProvisioned ? (user?.leadChildYearGroup || '') : '';

  const fetchProgress = async () => {
    try {
      const data = await get(`/onboarding/${user._id}/progress`, true);
      if (data) {
        setChildDetails(data.childDetails);
      }
    } catch (error) {
      console.error('Failed to fetch onboarding progress:', error);
    }
  };

  useEffect(() => {
    if (user?._id && !isPlatformPlan) {
      fetchProgress();
    }
  }, [user?._id, isPlatformPlan]);

  // Start the trial and finish onboarding. paymentMethodId is required by the server
  // for the 1-1 plan (CARD_REQUIRED) and omitted for the no-card group plans.
  // Each call is safe to repeat: create-trial returns the existing trialing sub and
  // complete returns early once isCompleted, so a retry after a partial failure is a no-op.
  const startTrialAndComplete = async (paymentMethodId) => {
    await post(
      '/payments/create-trial-subscription',
      paymentMethodId ? { paymentMethodId } : {},
      true,
    );

    await post(`/onboarding/${user._id}/complete`, {
      userId: user._id,
    }, true);

    const token = Cookies.get('token');
    const freshUserData = await verifyToken(token);
    login(freshUserData);

    navigate('/upcoming-lessons');
  };

  // Single-step classroom onboarding: persist the schedule, start the trial, and
  // complete onboarding in one go from the Set Schedule page. Throws on failure so
  // ChildDetails can surface the error and let the parent fix their slots and retry.
  // 1-1 plan: the schedule is saved, then we pause for the (required) card step —
  // the trial only starts once the card is on file.
  const handleComplete = async (stepData) => {
    await post(`/onboarding/${user._id}/save`, {
      step: ONBOARDING_STEPS.SCHEDULE,
      data: stepData,
    }, true);

    if (isOneToOnePlan) {
      setAwaitingCard(true);
      return;
    }

    await startTrialAndComplete();
  };

  const handleCardSaved = async (paymentMethod) => {
    setCardStepError('');
    setFinalising(true);
    try {
      await startTrialAndComplete(paymentMethod.id);
    } catch (err) {
      console.error('Failed to start 1-1 trial after card save:', err);
      setCardStepError(
        err?.response?.data?.message ||
          'Something went wrong starting your trial. Your card was saved — please try again.',
      );
      setFinalising(false);
    }
  };

  // Platform-only plans use a self-contained flow: child details (no class
  // selection) + card details. No schedule/review steps.
  if (isPlatformPlan) {
    return <PlatformOnboarding plan={user.selectedPlan} />;
  }

  // 1-1 plan card step: schedule is saved; collect the card, then start the trial.
  if (awaitingCard) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center p-6">
        <div className="w-full max-w-xl space-y-6 mt-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold">Add your card details</h1>
            <p className="text-gray-600 mt-2">
              The 1-1 Tuition Plan requires a card to start your 7-day free trial. You won't be
              charged today — billing (£150/month) only starts when your trial ends, and you can
              cancel anytime before then.
            </p>
          </div>

          {cardStepError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{cardStepError}</AlertDescription>
            </Alert>
          )}

          {finalising ? (
            <p className="text-center text-gray-600">Starting your trial…</p>
          ) : (
            <AddPaymentMethod
              customerId={user.stripeCustomerId}
              onSuccess={handleCardSaved}
              onClose={() => setAwaitingCard(false)}
              returnPath="/onboarding"
              submitLabel="Save card & start free trial"
              cancelLabel="Back"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ChildDetails
        onComplete={handleComplete}
        childDetails={childDetails}
        leadYearGroupSeed={leadYearGroupSeed}
        maxChildren={isOneToOnePlan ? 1 : 4}
        continueLabel={isOneToOnePlan ? 'Continue to card details' : 'Begin Free Trial'}
      />
    </div>
  );
}

export default OnboardingFlow;
